import { useAtom } from "jotai";

import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import { artboardBackgroundAtom } from "@design-lab/widgets/artboard";
import { useTransfersPolling } from "@design-lab/entities/transfers-lab/model/useTransfersPolling";
import { useTransfersArtboard } from "../model/useTransfersArtboard";
import { ArtboardBase } from "./ArtboardBase";
import type { TransfersLabArtboardProps } from "./ArtboardBase.types";

/**
 * Transfers Lab Artboard Wrapper
 *
 * Contains all Transfers Lab specific hooks and logic.
 * Passes data to ArtboardBase as props.
 */
export function TransfersLabArtboard({ readonly = false }: TransfersLabArtboardProps) {
  // Transfers Lab specific hooks
  const {
    imageUrl,
    hasImage,
    isLoading,
    imagePreview,
    startTime,
    artwork,
    withBgImage,
    noBgImage,
    backgroundPreference
  } = useTransfersArtboard();
  const [backgroundType, setBackgroundType] = useAtom(artboardBackgroundAtom);

  // Integrate fallback polling mechanism for transfers
  useTransfersPolling({
    artworkId: artwork?.id ? Number(artwork.id) : undefined,
    startTime,
    enabled: Boolean(artwork?.id && startTime)
  });

  // Prepare props for ArtboardBase
  const canvasProps = {
    surface: null,
    transferImageUrl: imageUrl,
    labType: "transfers" as const,
    withBgImage,
    noBgImage,
    backgroundPreference
  };

  // Determine if remove background should be shown
  const showRemoveBackground = Boolean(hasImage && !isLoading && artwork?.canRemoveBg);

  const controls = {
    showSideSwitcher: false,
    showZoom: true,
    showBackgroundToggle: hasImage && !isLoading,
    showPickAModel: false,
    showToolbar: false,
    showProductBrowser: false,
    showRemoveBackground,
    showSizePlacement: false
  };

  const loadingState = isLoading
    ? {
        isLoading: true as const,
        imagePreview,
        startTime,
        estimatedDuration: ARTWORK_PROCESSING.ESTIMATED_DURATION,
        steps: [
          { caption: "Removing background" },
          { caption: "Enhancing resolution" },
          { caption: "Boosting vibrancy" },
          { caption: "Refining edges" }
        ]
      }
    : undefined;

  return (
    <ArtboardBase
      readonly={readonly}
      canvasProps={canvasProps as any}
      controls={controls}
      backgroundType={backgroundType}
      onBackgroundChange={setBackgroundType}
      loadingState={loadingState as any}
      showPlaceholder={!hasImage && !isLoading}
      placeholderText="Image preview will appear here once uploaded"
      zoomConfig={{ initialZoom: 1, maxZoom: 10 }}
      blurCanvas={isLoading && hasImage}
      previewURL={imagePreview?.url}
    />
  );
}
